import {
  columnOf,
  taskPriorities,
  taskPriorityLabels,
  taskTypeLabels,
  taskTypes,
  type Task,
  type TaskColumn,
} from '@nexo/types';
import { AvatarStack, SegmentedControl, Select, cn } from '@nexo/ui';
import { ArrowDown, ArrowUp, Columns3, List, Search } from 'lucide-react';
import { useState, type MouseEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useTeam } from '../../layouts/TeamContext';
import { useColumns } from '../../lib/queries';
import { TaskBoard } from '../tasks/TaskBoard';
import { PointsBadge, PriorityIcon, StatusIcon, TaskKey, TypeIcon } from '../tasks/TaskIcons';
import { DueBadge } from '../tasks/TaskMeta';
import { columnName, filterTasks } from '../tasks/taskUtils';
import { useUpdateTask } from '../tasks/useTaskMutations';
import { pointsOf } from './sprintUtils';

type Layout = 'board' | 'list';
type SortKey = 'key' | 'title' | 'status' | 'priority' | 'points';
const LAYOUT_KEY = 'nexo.sprint.layout';

const priorityRank = Object.fromEntries(taskPriorities.map((p, i) => [p, i]));

function readLayout(): Layout {
  try {
    return localStorage.getItem(LAYOUT_KEY) === 'list' ? 'list' : 'board';
  } catch {
    return 'board';
  }
}

/** Every task of the sprint, as its board or as a sortable list, with shared filters. */
export function SprintTasks({ sprintId, tasks }: { sprintId: string; tasks: Task[] }) {
  const { t } = useTranslation();
  const { team, members } = useTeam();
  const [layout, setLayout] = useState<Layout>(readLayout);
  const [query, setQuery] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [type, setType] = useState('');

  const shown = filterTasks(tasks, {
    query,
    taskKey: team.taskKey,
    assigneeId: assigneeId || null,
  }).filter((task) => !type || task.type === type);

  return (
    <section className="overflow-hidden rounded-2xl border border-border bg-surface shadow-panel">
      <header className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2.5">
        <h3 className="mr-1 text-sm font-semibold">{t('Tareas del sprint')}</h3>
        <span className="text-xs text-muted tabular-nums">
          {shown.length === tasks.length
            ? `${tasks.length} · ${t('{{points}} pts', { points: pointsOf(tasks) })}`
            : `${t('{{shown}} de {{total}}', { shown: shown.length, total: tasks.length })} · ${t(
                '{{points}} pts',
                { points: pointsOf(shown) },
              )}`}
        </span>
        <span className="flex-1" />
        <label className="relative w-full sm:w-48">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-subtle" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('Filtrar…')}
            aria-label={t('Filtrar tareas del sprint')}
            className="h-8 w-full rounded-lg bg-surface-2 pr-2 pl-8 text-sm ring-1 ring-inset ring-border placeholder:text-subtle focus:ring-accent focus:outline-none"
          />
        </label>
        <div className="w-44">
          <Select
            aria-label={t('Responsable')}
            variant="subtle"
            size="sm"
            value={assigneeId}
            onChange={setAssigneeId}
            options={[
              { value: '', label: t('Todas las personas') },
              ...members.map((m) => ({ value: m.id, label: m.displayName })),
            ]}
          />
        </div>
        <div className="w-36">
          <Select
            aria-label={t('Tipo')}
            variant="subtle"
            size="sm"
            value={type}
            onChange={setType}
            options={[
              { value: '', label: t('Todos los tipos') },
              ...taskTypes.map((taskType) => ({
                value: taskType,
                label: t(taskTypeLabels[taskType], { context: 'taskType' }),
                icon: <TypeIcon type={taskType} />,
              })),
            ]}
          />
        </div>
        <SegmentedControl<Layout>
          label={t('Vista')}
          size="sm"
          value={layout}
          onChange={(next) => {
            setLayout(next);
            try {
              localStorage.setItem(LAYOUT_KEY, next);
            } catch {
              // Private mode: the choice just isn't remembered.
            }
          }}
          options={[
            { value: 'board', label: t('Tablero'), icon: <Columns3 /> },
            { value: 'list', label: t('Lista'), icon: <List /> },
          ]}
        />
      </header>
      {layout === 'board' ? (
        <div className="h-[min(70vh,44rem)] min-h-[26rem]">
          <TaskBoard tasks={shown} sprintId={sprintId} />
        </div>
      ) : (
        <TaskTable tasks={shown} />
      )}
    </section>
  );
}

function TaskTable({ tasks }: { tasks: Task[] }) {
  const { t } = useTranslation();
  const { team, openTask } = useTeam();
  const columns = useColumns(team.id).data ?? [];
  const update = useUpdateTask();
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'status', desc: false });

  const columnIndex = (task: Task) => {
    const column = columnOf(task, columns);
    return column ? columns.indexOf(column) : 0;
  };
  const value = (task: Task): number | string => {
    switch (sort.key) {
      case 'key':
        return task.number;
      case 'title':
        return task.title.toLowerCase();
      case 'status':
        return columnIndex(task);
      case 'priority':
        return priorityRank[task.priority] ?? 0;
      case 'points':
        return task.storyPoints ?? -1;
    }
  };
  const sorted = [...tasks].sort((a, b) => {
    const [x, y] = [value(a), value(b)];
    const order = x < y ? -1 : x > y ? 1 : a.number - b.number;
    return sort.desc ? -order : order;
  });
  const header = (key: SortKey, label: string, className?: string) => (
    <th
      className={cn('px-3 py-2 font-medium', className)}
      aria-sort={sort.key === key ? (sort.desc ? 'descending' : 'ascending') : undefined}
    >
      <button
        type="button"
        onClick={() => setSort((s) => ({ key, desc: s.key === key ? !s.desc : false }))}
        className="inline-flex items-center gap-1 hover:text-text"
      >
        {label}
        {sort.key === key &&
          (sort.desc ? <ArrowDown className="size-3" /> : <ArrowUp className="size-3" />)}
      </button>
    </th>
  );

  if (tasks.length === 0) {
    return (
      <p className="px-4 py-10 text-center text-sm text-muted">{t('No hay tareas que mostrar.')}</p>
    );
  }

  return (
    <div className="scrollbar-thin overflow-x-auto">
      <table className="w-full min-w-[760px] text-sm">
        <thead className="bg-surface-2/60 text-left text-xs text-muted">
          <tr>
            {header('key', t('Clave'), 'w-24')}
            {header('title', t('Título'))}
            {header('status', t('Estado'), 'w-44')}
            <th className="w-32 px-3 py-2 font-medium">{t('Responsables')}</th>
            {header('priority', t('Prioridad'), 'w-32')}
            {header('points', t('Puntos'), 'w-20 text-right')}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {sorted.map((task) => {
            const done = task.status === 'done';
            return (
              <tr
                key={task.id}
                onClick={() => openTask({ mode: 'edit', taskId: task.id })}
                className="cursor-pointer hover:bg-surface-2/60"
              >
                <Cell>
                  <span className="flex items-center gap-1.5">
                    <TypeIcon type={task.type} />
                    <TaskKey task={task} />
                  </span>
                </Cell>
                <Cell>
                  <span className="flex min-w-0 items-center gap-2">
                    <span className={cn('truncate', done && 'text-subtle line-through')}>
                      {task.title}
                    </span>
                    {!done && <DueBadge task={task} />}
                  </span>
                </Cell>
                <Cell onClick={(e) => e.stopPropagation()}>
                  <ColumnPicker
                    task={task}
                    columns={columns}
                    onChange={(columnId) => update.mutate({ id: task.id, input: { columnId } })}
                  />
                </Cell>
                <Cell>
                  {task.assignees.length > 0 ? (
                    <AvatarStack people={task.assignees} max={3} />
                  ) : (
                    <span className="text-xs text-subtle">{t('Sin asignar')}</span>
                  )}
                </Cell>
                <Cell>
                  <span className="flex items-center gap-1.5 text-muted">
                    <PriorityIcon priority={task.priority} showNormal />
                    {t(taskPriorityLabels[task.priority], { context: 'priority' })}
                  </span>
                </Cell>
                <Cell className="text-right">
                  {task.storyPoints === null ? (
                    <span className="text-xs text-subtle">—</span>
                  ) : (
                    <PointsBadge points={task.storyPoints} />
                  )}
                </Cell>
              </tr>
            );
          })}
        </tbody>
        <tfoot className="border-t border-border bg-surface-2/40 text-xs text-muted">
          <tr>
            <td className="px-3 py-2" colSpan={5}>
              {tasks.length === 1 ? t('1 tarea') : t('{{count}} tareas', { count: tasks.length })} ·{' '}
              {t('{{count}} hechas', {
                count: tasks.filter((task) => task.status === 'done').length,
              })}
            </td>
            <td className="px-3 py-2 text-right font-medium text-text tabular-nums">
              {t('{{points}} pts', { points: pointsOf(tasks) })}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function Cell({
  children,
  className,
  onClick,
}: {
  children: ReactNode;
  className?: string;
  onClick?: (event: MouseEvent) => void;
}) {
  return (
    <td className={cn('px-3 py-2 align-middle', className)} onClick={onClick}>
      {children}
    </td>
  );
}

function ColumnPicker({
  task,
  columns,
  onChange,
}: {
  task: Task;
  columns: TaskColumn[];
  onChange: (columnId: string) => void;
}) {
  const { t } = useTranslation();
  const current = columnOf(task, columns);
  return (
    <Select
      aria-label={t('Estado de {{title}}', { title: task.title })}
      variant="ghost"
      size="sm"
      value={current?.id ?? ''}
      onChange={onChange}
      options={columns.map((c) => ({
        value: c.id,
        label: columnName(c.name),
        icon: <StatusIcon status={c.category} />,
      }))}
    />
  );
}
